<template>
  <div v-show="showStreamManager">
    <q-card square flat bordered class="my-card bg-grey-1 vertical-bottom">
      <q-toolbar>
        <q-toolbar-title class="text-weight-bold text-grey-brand">
          Stream Manager
        </q-toolbar-title>
        <q-btn flat round dense icon="refresh" @click="refresh">
          <q-tooltip>Refresh sessions</q-tooltip>
        </q-btn>
        <q-btn flat round dense icon="close" @click="onClose" />
      </q-toolbar>

      <!-- Start new session -->
      <q-card-section class="q-pt-none q-pb-sm">
        <div class="row items-center q-gutter-sm">
          <q-select
            v-model="selectedDevice"
            :options="filteredDeviceOptions"
            label="Select device"
            dense
            outlined
            option-label="label"
            option-value="uid"
            emit-value
            map-options
            use-input
            input-debounce="200"
            class="col"
            :loading="loadingDevices"
            @popup-show="fetchDevices"
            @filter="onFilterDevices"
          >
            <template #option="scope">
              <q-item v-bind="scope.itemProps">
                <q-item-section>
                  <q-item-label>{{ scope.opt.label }}</q-item-label>
                </q-item-section>
                <q-item-section side>
                  <q-badge
                    :color="scope.opt.isConnected ? 'green' : 'grey'"
                    :label="scope.opt.isConnected ? 'online' : 'offline'"
                  />
                </q-item-section>
              </q-item>
            </template>
          </q-select>
          <q-btn
            dense
            :outline="!onlineOnly"
            :color="onlineOnly ? 'green' : 'grey'"
            icon="wifi"
            @click="onlineOnly = !onlineOnly"
          >
            <q-tooltip>{{
              onlineOnly ? 'Showing online only' : 'Showing all devices'
            }}</q-tooltip>
          </q-btn>
          <q-btn
            dense
            color="primary"
            label="Start"
            :disable="selectedDevice === null"
            @click="startSession"
          />
        </div>
      </q-card-section>

      <q-separator />

      <q-card-section v-if="activeDeviceSessions.length === 0">
        <div class="text-grey-6 text-center q-py-md">
          No active stream sessions
        </div>
      </q-card-section>

      <q-list v-else separator>
        <q-item
          v-for="device in activeDeviceSessions"
          :key="device.uid"
          class="q-py-sm"
          :class="{ 'stream-active-item': isStreamOpen(device.uid) }"
        >
          <q-item-section>
            <q-item-label class="text-weight-medium">
              UID {{ device.uid }}
            </q-item-label>
            <q-item-label caption>
              {{ device.activeSessionCount }}
              {{ device.activeSessionCount === 1 ? 'viewer' : 'viewers' }}
            </q-item-label>
            <q-item-label
              v-for="session in device.sessions"
              :key="session.streamSessionId"
              caption
              class="q-ml-sm"
            >
              {{ session.userName }}
              <span class="text-grey-5">
                — joined {{ formatTime(session.createdDate) }}
              </span>
            </q-item-label>
          </q-item-section>
          <q-item-section side style="min-width: 80px">
            <div class="row no-wrap items-center justify-end">
              <q-btn
                v-if="!isStreamOpen(device.uid)"
                flat
                dense
                color="primary"
                icon="play_arrow"
                @click="openStream(device.uid)"
              >
                <q-tooltip>Open stream</q-tooltip>
              </q-btn>
              <q-icon
                v-else
                name="sensors"
                color="green"
                size="sm"
                class="q-mr-xs"
              >
                <q-tooltip>Active in realtime plot</q-tooltip>
              </q-icon>
              <q-btn
                v-if="isViewerOn(device)"
                flat
                dense
                color="negative"
                icon="logout"
                @click="leaveStream(device.uid)"
              >
                <q-tooltip>Leave session</q-tooltip>
              </q-btn>
              <div v-else style="width: 33px" />
            </div>
          </q-item-section>
        </q-item>
      </q-list>
    </q-card>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { Device } from '../models/Device';

export default defineComponent({
  name: 'StreamManager',
  data() {
    return {
      selectedDevice: null as number | null,
      loadingDevices: false,
      onlineOnly: false,
      uidFilter: '',
    };
  },
  computed: {
    showStreamManager(): boolean {
      return this.$store.state.showStreamManager;
    },
    activeDeviceSessions(): any[] {
      return this.$store.state.streamSession.activeDeviceSessions;
    },
    activeStreamUid(): number | null {
      return this.$store.state.realtimeStreaming.activeStreamUid;
    },
    deviceOptions(): { uid: number; label: string; isConnected: boolean }[] {
      const devices: Device[] = this.$store.state.device.devices || [];
      return devices
        .filter((d) => !this.onlineOnly || d.isConnected)
        .map((d) => ({
          uid: d.uid,
          label: `UID ${d.uid}`,
          isConnected: !!d.isConnected,
        }))
        .sort((a, b) => a.uid - b.uid);
    },
    filteredDeviceOptions(): {
      uid: number;
      label: string;
      isConnected: boolean;
    }[] {
      if (!this.uidFilter) return this.deviceOptions;
      return this.deviceOptions.filter((d) =>
        String(d.uid).includes(this.uidFilter)
      );
    },
  },
  methods: {
    refresh() {
      this.$store.dispatch('fetchActiveStreamSessions');
    },
    onClose() {
      this.$store.dispatch('hideStreamManager');
    },
    fetchDevices() {
      this.loadingDevices = true;
      this.$store.dispatch('getDevices');
      // getDevices is fire-and-forget; give it a moment then clear the spinner
      setTimeout(() => {
        this.loadingDevices = false;
      }, 1500);
    },
    onFilterDevices(val: string, update: (fn: () => void) => void) {
      update(() => {
        this.uidFilter = val;
      });
    },
    async startSession() {
      if (this.selectedDevice === null) return;
      const uid = this.selectedDevice;
      this.selectedDevice = null;
      const joined = await this.$store.dispatch('joinStreamSession', uid);
      if (joined) {
        this.$store.dispatch('openAdditionalStream', uid);
      }
    },
    async openStream(uid: number) {
      const joined = await this.$store.dispatch('joinStreamSession', uid);
      if (joined) {
        this.$store.dispatch('openAdditionalStream', uid);
      }
    },
    leaveStream(uid: number) {
      this.$store.dispatch('leaveStreamSession', uid);
      this.$store.dispatch('closeStream', uid);
    },
    isStreamOpen(uid: number): boolean {
      return this.activeStreamUid === uid;
    },
    isViewerOn(device: any): boolean {
      const userId = this.$store.state.user?.id;
      if (!userId) return false;
      return device.sessions.some((s: any) => s.userId === userId);
    },
    formatTime(isoString: string): string {
      if (!isoString) return '';
      const d = new Date(isoString);
      return d.toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      });
    },
  },
});
</script>

<style scoped>
.stream-active-item {
  background-color: rgba(33, 150, 243, 0.08);
  border-left: 3px solid #2196f3;
}
</style>
